import React from "react";
import { ArrowRight, Microscope, Sparkles, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CienciaECuidadoParaSeusCabelos() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="ciência-e-cuidado-para-seus-cabelos" ref={sectionRef} className="relative w-full overflow-hidden bg-background py-16 lg:py-24 xl:py-32">
      {}
      <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div className={`flex flex-col gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-4">
              <Badge variant="outline" className="w-fit px-4 py-1.5 text-sm border-primary/20 text-primary bg-primary/5 rounded-full backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5 mr-2 fill-primary/20" />
                Tricologia Avançada & Bem-estar
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-medium leading-[1.1] text-foreground tracking-tight">
                A união perfeita entre{" "}
                <span className="italic text-primary font-light block mt-2">Ciência</span>{" "}
                e{" "}
                <span className="italic text-primary font-light">Cuidado</span>.
              </h1>
            </div>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl font-sans">
              Combinamos tecnologia de diagnóstico tricoscópico de alta precisão com protocolos terapêuticos humanizados. 
              Recupere a saúde dos seus fios com tratamentos baseados em evidências científicas.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button asChild size="lg" className="rounded-full h-14 px-8 text-base shadow-[0_0_25px_-5px_rgba(var(--primary),0.4)] hover:shadow-[0_0_35px_-5px_rgba(var(--primary),0.5)] transition-all duration-300">
                <Link to="/SchedulingPage">
                  Agendar Avaliação
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="rounded-full h-14 px-8 text-base border-primary/20 hover:bg-primary/5 hover:text-primary transition-colors">
                <Link to="/ProtocolsPage">
                  Conhecer Protocolos
                </Link>
              </Button>
            </div>

            {}
            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-border/40">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-secondary/10 text-secondary">
                  <Microscope className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground">Diagnóstico</span>
                  <span className="text-sm text-muted-foreground">Scanner Ticoscópico</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-secondary/10 text-secondary">
                  <Activity className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground">Resultados</span>
                  <span className="text-sm text-muted-foreground">Baseados em Evidências</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`relative transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            {}
            <div className="relative z-10 w-full aspect-[4/5] md:aspect-square lg:aspect-[4/5]">
              <div className="absolute inset-0 rounded-[2rem_8rem_2rem_8rem] overflow-hidden shadow-2xl border-4 border-white/50">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/hero_woman_healthy_hair.png" alt="Mulher com cabelos saudáveis e brilhantes após tratamento capilar" className="w-full h-full object-cover scale-105 hover:scale-110 transition-transform duration-700 ease-in-out" />
                
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>

              {}
              <Card className="absolute -bottom-6 -left-6 md:bottom-8 md:-left-8 w-64 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] border-white/60 backdrop-blur-md bg-white/90 animate-in fade-in zoom-in duration-1000 delay-700 fill-mode-forwards">
                <CardContent className="p-4 flex gap-4 items-center">
                  <div className="relative h-16 w-16 rounded-lg overflow-hidden border border-border shrink-0 bg-black">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/microscopic_hair_scan.png" alt="Análise microscópica do fio" className="w-full h-full object-cover opacity-80" />
                    {}
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.8)] animate-[scan_2s_ease-in-out_infinite]" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Análise Ativa</span>
                    <span className="text-sm font-medium text-foreground leading-tight">Saúde do folículo: <span className="text-green-600">98%</span></span>
                    <span className="text-[10px] text-muted-foreground mt-1">Densidade recuperada</span>
                  </div>
                </CardContent>
              </Card>

              {}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-xl -z-10" />
            </div>
          </div>

        </div>
      </div>

      {}
      <style>{`
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </section>;
}